import { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  BadgeCheck, QrCode, Globe2, FileSpreadsheet, Bell, ArrowRight,
  Phone, Mail, MapPin, Menu, X,
} from "lucide-react";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";

export default function Landing({ anchor }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [plans, setPlans] = useState([]);
  const lang = (i18n.language || "en").split("-")[0];

  // Load editable landing content from the CMS with i18n bundle as fallback.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/landing/content`, { params: { lang } })
      .then(({ data }) => {
        if (!cancelled) setContent(data);
      })
      .catch(() => {
        if (!cancelled) setContent(null);
      });
    return () => { cancelled = true; };
  }, [lang]);

  // Load live plans from subscription_plans (single source of truth).
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/subscription-plans`, { params: { lang } })
      .then(({ data }) => {
        if (!cancelled) setPlans(data.items || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lang]);

  useEffect(() => {
    if (anchor) {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [anchor]);

  // Small helper: prefer CMS text, fall back to i18n bundle.
  const c = (key, fallbackKey) => {
    if (content && content[key]) return content[key];
    return t(fallbackKey);
  };

  const features = [
    { icon: BadgeCheck, k: "swedish" },
    { icon: QrCode, k: "dualQr" },
    { icon: Globe2, k: "multiCurrency" },
    { icon: Globe2, k: "triLang" },
    { icon: FileSpreadsheet, k: "exports" },
    { icon: Bell, k: "reminders" },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <img src="/branding/icon-mark.jpeg" alt="InvoicePro" className="h-8 w-8 rounded" />
            <span className="font-heading text-xl font-semibold">InvoicePro</span>
          </Link>
          <nav className="hidden items-center gap-6 md:flex">
            <a href="#features" className="text-sm hover:text-indigo-700">{t("landing.nav.features")}</a>
            <a href="#pricing" className="text-sm hover:text-indigo-700">{t("landing.nav.pricing")}</a>
            <a href="#contact" className="text-sm hover:text-indigo-700">{t("landing.nav.contact")}</a>
            <LanguageSwitcher testId="landing-lang" />
            {user ? (
              <Button onClick={() => navigate("/app")} data-testid="landing-goto-app" className="bg-slate-900 text-white hover:bg-slate-800">
                {t("landing.nav.goToApp")} <ArrowRight className="ms-1 h-4 w-4" />
              </Button>
            ) : (
              <>
                <NavLink to="/login" className="text-sm hover:text-indigo-700" data-testid="landing-login">{t("landing.nav.login")}</NavLink>
                <Button onClick={() => navigate("/signup")} data-testid="landing-signup" className="bg-indigo-600 text-white hover:bg-indigo-700">
                  {t("landing.nav.signup")}
                </Button>
              </>
            )}
          </nav>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-md p-2 md:hidden"
            data-testid="landing-menu-toggle"
            aria-label="menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden" data-testid="landing-mobile-menu">
            <a href="#features" onClick={() => setMenuOpen(false)} className="block py-2">{t("landing.nav.features")}</a>
            <a href="#pricing" onClick={() => setMenuOpen(false)} className="block py-2">{t("landing.nav.pricing")}</a>
            <a href="#contact" onClick={() => setMenuOpen(false)} className="block py-2">{t("landing.nav.contact")}</a>
            <div className="my-2"><LanguageSwitcher testId="landing-lang-m" /></div>
            {user ? (
              <Button onClick={() => navigate("/app")} className="mt-2 w-full">{t("landing.nav.goToApp")}</Button>
            ) : (
              <div className="mt-2 flex gap-2">
                <Button onClick={() => navigate("/login")} variant="outline" className="flex-1">{t("landing.nav.login")}</Button>
                <Button onClick={() => navigate("/signup")} className="flex-1 bg-indigo-600 text-white">{t("landing.nav.signup")}</Button>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 md:py-24">
        <div className="grid gap-10 md:grid-cols-2 md:items-center">
          <div className="space-y-5">
            <span
              className="inline-flex rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-indigo-700"
              data-testid="landing-hero-badge"
            >
              {c("hero_badge", "landing.hero.badge")}
            </span>
            <h1
              className="font-heading text-4xl leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
              data-testid="landing-hero-title"
              style={{ whiteSpace: "pre-line" }}
            >
              {c("hero_title", "landing.hero.title")}
            </h1>
            <p
              className="max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg"
              style={{ whiteSpace: "pre-line" }}
              data-testid="landing-hero-subtitle"
            >
              {c("hero_subtitle", "landing.hero.subtitle")}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate("/signup")} data-testid="hero-cta-signup" className="bg-indigo-600 text-white hover:bg-indigo-700">
                {c("hero_cta_primary", "landing.hero.ctaPrimary")} <ArrowRight className="ms-1 h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-cta-features">
                {c("hero_cta_secondary", "landing.hero.ctaSecondary")}
              </Button>
            </div>
          </div>
          <div className="order-first md:order-last">
            <img
              src="/branding/primary-logo.jpeg"
              alt="InvoicePro"
              className="mx-auto w-full max-w-md rounded-2xl object-contain shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-slate-100 bg-slate-50 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mb-10 max-w-xl">
            <h2 className="font-heading text-3xl sm:text-4xl" data-testid="landing-features-title">
              {c("features_title", "landing.features.title")}
            </h2>
            <p className="mt-2 text-slate-600" style={{ whiteSpace: "pre-line" }}>
              {c("features_subtitle", "landing.features.subtitle")}
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="landing-features">
            {features.map(({ icon: Icon, k }) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-5 transition hover:shadow-md">
                <Icon className="h-6 w-6 text-indigo-600" />
                <h3 className="mt-3 font-semibold">{t(`landing.features.items.${k}.title`)}</h3>
                <p className="mt-1 text-sm text-slate-600">{t(`landing.features.items.${k}.body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="mb-10 font-heading text-3xl sm:text-4xl" data-testid="landing-how-title">
            {c("how_title", "landing.how.title")}
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-6">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white">{i}</div>
                <h3 className="mt-4 font-semibold">
                  {c(`how_step${i}_title`, `landing.how.step${i}.title`)}
                </h3>
                <p className="mt-1 text-sm text-slate-600" style={{ whiteSpace: "pre-line" }}>
                  {c(`how_step${i}_body`, `landing.how.step${i}.body`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing — pulled live from subscription_plans */}
      <section id="pricing" className="border-t border-slate-100 bg-slate-50 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mb-10 max-w-xl">
            <h2 className="font-heading text-3xl sm:text-4xl" data-testid="landing-pricing-title">
              {c("pricing_title", "landing.pricing.title")}
            </h2>
            <p className="mt-2 text-slate-600" style={{ whiteSpace: "pre-line" }}>
              {c("pricing_subtitle", "landing.pricing.subtitle")}
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3" data-testid="landing-pricing">
            {plans.map((plan) => (
              <div
                key={plan.code}
                className={`rounded-xl border p-6 ${plan.code === "free" ? "border-indigo-600 bg-white ring-2 ring-indigo-100" : "border-slate-200 bg-white"}`}
                data-testid={`landing-plan-${plan.code}`}
              >
                <h3 className="font-heading text-2xl">{plan.name}</h3>
                <div className="mt-1 text-sm text-slate-500">{plan.description}</div>
                <div className="mt-4 text-3xl font-bold tabular-nums text-slate-900">
                  {plan.code === "free" ? t("signup.valueProps.free") : `${plan.price_monthly} ${plan.currency}`}
                  {plan.code !== "free" && (
                    <span className="text-sm font-normal text-slate-500">{t("signup.valueProps.perMonth")}</span>
                  )}
                </div>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {(plan.features || []).map((f) => (
                    <li key={f.key} className="flex gap-2">
                      {f.included ? (
                        <BadgeCheck className="h-4 w-4 text-indigo-600 shrink-0" />
                      ) : (
                        <X className="h-4 w-4 text-slate-300 shrink-0" />
                      )}
                      <span className={f.included ? "" : "text-slate-400 line-through"}>{f.label}</span>
                    </li>
                  ))}
                </ul>
                {plan.code === "free" ? (
                  <Button onClick={() => navigate("/signup")} className="mt-6 w-full bg-indigo-600 text-white hover:bg-indigo-700" data-testid="pricing-free-cta">
                    {t("landing.pricing.plans.free.cta")}
                  </Button>
                ) : (
                  <a href="mailto:info@nordicscandiskroyal.se?subject=InvoicePro%20Upgrade" className="mt-6 inline-flex w-full items-center justify-center rounded-md border border-slate-300 py-2 text-sm hover:bg-slate-50">
                    {t("landing.pricing.contactUs")}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="mb-10 font-heading text-3xl sm:text-4xl" data-testid="landing-contact-title">
            {c("contact_title", "landing.contact.title")}
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4" data-testid="landing-contact">
              <div>
                <div className="mb-2 flex items-center gap-2 font-semibold">
                  <Phone className="h-4 w-4 text-indigo-600" />
                  {c("contact_sales", "landing.contact.sales")}
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <div className="text-slate-500">{t("landing.contact.regionEuUsUk")}</div>
                    <a href={`tel:${(content?.contact_phone_eu || "").replace(/\s/g, "")}`} className="font-mono">
                      {c("contact_phone_eu", "landing.contact.regionEuUsUk")}
                    </a>
                  </div>
                  <div>
                    <div className="text-slate-500">{t("landing.contact.regionGcc")}</div>
                    <a href={`tel:${(content?.contact_phone_gcc || "").replace(/\s/g, "")}`} className="font-mono">
                      {c("contact_phone_gcc", "landing.contact.regionGcc")}
                    </a>
                  </div>
                  <div>
                    <div className="text-slate-500">{t("landing.contact.regionLevant")}</div>
                    <a href={`tel:${(content?.contact_phone_levant || "").replace(/\s/g, "")}`} className="font-mono">
                      {c("contact_phone_levant", "landing.contact.regionLevant")}
                    </a>
                  </div>
                </div>
              </div>
              <div>
                <div className="mb-2 flex items-center gap-2 font-semibold">
                  <Mail className="h-4 w-4 text-indigo-600" />
                  {c("contact_support", "landing.contact.support")}
                </div>
                <a
                  href={`mailto:${content?.contact_email || "info@nordicscandiskroyal.se"}`}
                  className="text-indigo-700 hover:underline"
                  data-testid="contact-email"
                >
                  {c("contact_email", "landing.contact.support")}
                </a>
              </div>
              <div>
                <div className="mb-2 flex items-center gap-2 font-semibold">
                  <MapPin className="h-4 w-4 text-indigo-600" />
                  {t("landing.contact.address")}
                </div>
                <div className="text-sm" style={{ whiteSpace: "pre-line" }}>
                  {c("contact_address", "landing.contact.address")}
                </div>
              </div>
            </div>
            <iframe
              title="Malmö office"
              className="h-full min-h-[300px] w-full rounded-xl border border-slate-200"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://maps.google.com/maps?q=Per+Albin+Hanssons+v%C3%A4g+28B,+214+32+Malm%C3%B6&t=&z=15&ie=UTF8&iwloc=&output=embed"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-900 py-8 text-slate-300">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-4 text-center sm:flex-row sm:justify-between sm:text-start">
          <div className="flex items-center gap-3">
            <img src="/branding/vendor-mark.jpeg" alt="Nordic Scandisk Royal" className="h-10 w-auto rounded" />
            <div className="text-xs" data-testid="landing-footer-tagline">
              {c("footer_tagline", "landing.footer.terms")}
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-4 text-xs">
            <NavLink to="/legal/terms" className="hover:text-white">{t("landing.footer.terms")}</NavLink>
            <NavLink to="/legal/privacy" className="hover:text-white">{t("landing.footer.privacy")}</NavLink>
            <a href="#contact" className="hover:text-white">{t("landing.footer.contact")}</a>
            <NavLink to="/login" className="hover:text-white">{t("landing.footer.login")}</NavLink>
          </div>
        </div>
      </footer>
    </div>
  );
}
